import { jsPDF } from 'jspdf';
import { PAGE_SIZES } from './constants';

export const getPageDimensions = (settings, imgAspectRatio) => {
  let width;
  let height;

  if (settings.pageSize === 'Custom') {
    const factor =
      settings.customUnit === 'in'
        ? 25.4
        : settings.customUnit === 'px'
          ? 0.264583
          : 1;

    width = settings.customWidth * factor;
    height = settings.customHeight * factor;
  } else {
    width = PAGE_SIZES[settings.pageSize].width;
    height = PAGE_SIZES[settings.pageSize].height;
  }

  let orientation = settings.orientation;

  if (orientation === 'auto') {
    orientation = imgAspectRatio > 1 ? 'landscape' : 'portrait';
  }

  if (orientation === 'landscape' && width < height) {
    [width, height] = [height, width];
  } else if (orientation === 'portrait' && width > height) {
    [width, height] = [height, width];
  }

  return {
    w: width,
    h: height,
    orientation,
  };
};

export const calculateSequence = (images, settings) => {
  const items = [];

  images.forEach((image) => {
    if (settings.blankPages === 'before' || settings.blankPages === 'both') {
      items.push({
        type: 'blank',
        id: `blank-before-${image.id}`,
        parentId: image.id,
        aspectRatio: 0.7,
      });
    }

    items.push({
      type: 'image',
      ...image,
    });

    if (settings.blankPages === 'after' || settings.blankPages === 'both') {
      items.push({
        type: 'blank',
        id: `blank-after-${image.id}`,
        parentId: image.id,
        aspectRatio: 0.7,
      });
    }
  });

  const pages = [];
  const perPage = parseInt(settings.imagesPerPage, 10) || 1;

  for (let index = 0; index < items.length; index += perPage) {
    pages.push(items.slice(index, index + perPage));
  }

  return pages;
};

const getGridConfig = (count) => {
  if (count <= 1) return { rows: 1, cols: 1 };
  if (count === 2) return { rows: 2, cols: 1 };
  if (count <= 4) return { rows: 2, cols: 2 };
  if (count <= 6) return { rows: 3, cols: 2 };
  if (count <= 8) return { rows: 4, cols: 2 };
  return { rows: 4, cols: 3 };
};

export const generatePDF = async (images, settings, onProgress) => {
  if (images.length === 0) return;

  const pages = calculateSequence(images, settings);
  const firstPage = pages[0];
  const firstDimensions = getPageDimensions(
    settings,
    firstPage[0]?.aspectRatio || 1
  );

  const doc = new jsPDF({
    orientation: firstDimensions.orientation,
    unit: 'mm',
    format: [firstDimensions.w, firstDimensions.h],
  });

  const totalPages = pages.length;

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const pageItems = pages[pageIndex];
    const {
      w: pageWidth,
      h: pageHeight,
      orientation,
    } = getPageDimensions(
      settings,
      pageItems[0]?.aspectRatio || 1
    );

    if (pageIndex > 0) {
      doc.addPage([pageWidth, pageHeight], orientation);
    }

    const grid = getGridConfig(pageItems.length);
    const margin = Number(settings.margin) || 0;
    const cellWidth = (pageWidth - margin * 2) / grid.cols;
    const cellHeight = (pageHeight - margin * 2) / grid.rows;

    for (let itemIndex = 0; itemIndex < pageItems.length; itemIndex += 1) {
      const item = pageItems[itemIndex];

      if (item.type === 'blank') continue;

      const row = Math.floor(itemIndex / grid.cols);
      const column = itemIndex % grid.cols;
      const startX = margin + column * cellWidth;
      const startY = margin + row * cellHeight;
      const cellPadding = pageItems.length > 1 ? 2 : 0;
      const targetWidth = cellWidth - cellPadding * 2;
      const targetHeight = cellHeight - cellPadding * 2;
      const textHeight = settings.showDetails ? 8 : 0;
      const imageTargetHeight = Math.max(1, targetHeight - textHeight);

      if (settings.imageBackground) {
        doc.setFillColor(settings.imageBackground);
        doc.rect(
          startX + cellPadding,
          startY + cellPadding,
          targetWidth,
          imageTargetHeight,
          'F'
        );
      }

      let finalX;
      let finalY;
      let finalWidth;
      let finalHeight;

      if (settings.fitMode === 'fit') {
        const cellRatio = targetWidth / imageTargetHeight;

        if (item.aspectRatio > cellRatio) {
          finalWidth = targetWidth;
          finalHeight = targetWidth / item.aspectRatio;
        } else {
          finalHeight = imageTargetHeight;
          finalWidth = imageTargetHeight * item.aspectRatio;
        }

        finalX =
          startX +
          cellPadding +
          (targetWidth - finalWidth) / 2;
        finalY =
          startY +
          cellPadding +
          (imageTargetHeight - finalHeight) / 2;
      } else {
        finalWidth = targetWidth;
        finalHeight = imageTargetHeight;
        finalX = startX + cellPadding;
        finalY = startY + cellPadding;
      }

      doc.addImage(
        item.dataUrl,
        'JPEG',
        finalX,
        finalY,
        finalWidth,
        finalHeight
      );

      if (settings.showDetails) {
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);

        const detailText = `${item.name} (${Math.round(item.width)}x${Math.round(
          item.height
        )}px)`;
        const textWidth = doc.getTextWidth(detailText);

        doc.text(
          detailText,
          startX + cellPadding + (targetWidth - textWidth) / 2,
          startY + cellPadding + imageTargetHeight + 5
        );
      }
    }

    if (settings.showPageNumbers) {
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);

      const pageText = `Page ${pageIndex + 1} of ${totalPages}`;
      const textWidth = doc.getTextWidth(pageText);

      doc.text(
        pageText,
        (pageWidth - textWidth) / 2,
        pageHeight - 5
      );
    }

    onProgress(Math.round(((pageIndex + 1) / totalPages) * 100));
  }

  doc.save(settings.filename || 'images.pdf');
  onProgress(0);
};